import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import classService from '../services/classService';
import api from '../services/api';
import useAuth from '../hooks/useAuth';
import type { Pupil } from '../types';

export const ClassDetails: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const numericClassId = parseInt(classId || '0', 10);
  const { hasPermission } = useAuth();

  const [className, setClassName] = useState<string>('');
  const [pupils, setPupils] = useState<Pupil[]>([]);
  const [classesList, setClassesList] = useState<{ id: number; class_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedPeriod, setSelectedPeriod] = useState('1_1');
  const [selectedYear, setSelectedYear] = useState('1');
  const [academicYears, setAcademicYears] = useState<{ id: number; year_name: string }[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Bulk Selection
  const [selectedPupilIds, setSelectedPupilIds] = useState<number[]>([]);

  // Modals Visibility
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);

  // Registration Form
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regAdmissionNumber, setRegAdmissionNumber] = useState('');
  const [regSex, setRegSex] = useState<'M' | 'F'>('M');
  const [regDOB, setRegDOB] = useState('');
  const [regParent, setRegParent] = useState('');
  const [regNationality, setRegNationality] = useState('Zambian');

  // Promotion Form
  const [promoteStatus, setPromoteStatus] = useState<string>('promoted');
  const [targetClassId, setTargetClassId] = useState<string>('');
  const [targetYearId, setTargetYearId] = useState<string>('');

  const fetchPupilsAndYears = async () => {
    try {
      const [pupilsRes, formRes, classesRes] = await Promise.all([
        classService.getClassPupils(numericClassId),
        api.get('/manage-classes/form-data'),
        classService.getMyClasses()
      ]);
      setPupils(pupilsRes);
      setClassesList(classesRes);
      if (formRes.data.success) {
        setAcademicYears(formRes.data.data.academic_years);
        if (formRes.data.data.academic_years.length > 0) {
          setSelectedYear(String(formRes.data.data.academic_years[0].id));
          setTargetYearId(String(formRes.data.data.academic_years[0].id));
        }
      }
      const activeClass = classesRes.find(c => c.id === numericClassId);
      if (activeClass) {
        setClassName(activeClass.class_name);
      } else {
        setClassName(`Class #${numericClassId}`);
      }
    } catch (err: any) {
      setError('Failed to fetch class pupils and academic years.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPupilsAndYears();
  }, [numericClassId]);

  const handleLaunchGrading = () => {
    const [term, type] = selectedPeriod.split('_');
    navigate(`/classes/${numericClassId}/grade?term=${term}&type=${type}&year=${selectedYear}`);
  };

  const handleLaunchAttendance = () => {
    navigate(`/classes/${numericClassId}/attendance?date=${selectedDate}`);
  };

  const handleSelectPupil = (id: number) => {
    setSelectedPupilIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedPupilIds.length === pupils.length) {
      setSelectedPupilIds([]);
    } else {
      setSelectedPupilIds(pupils.map(p => p.id));
    }
  };

  const handleRegisterStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        first_name: regFirstName,
        last_name: regLastName,
        admission_number: regAdmissionNumber,
        sex: regSex,
        date_of_birth: regDOB || null,
        parent_guardian_name: regParent || null,
        nationality: regNationality,
        class_id: numericClassId,
        academic_year_id: parseInt(selectedYear, 10)
      };

      const res = await api.post('/students/register', payload);
      if (res.data.success) {
        setIsRegisterModalOpen(false);
        setRegFirstName('');
        setRegLastName('');
        setRegAdmissionNumber('');
        setRegSex('M');
        setRegDOB('');
        setRegParent('');
        setRegNationality('Zambian');
        setLoading(true);
        fetchPupilsAndYears();
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to register student.');
    }
  };

  const handleBulkPromote = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        student_ids: selectedPupilIds,
        status: promoteStatus,
        class_id: ['promoted', 'repeated', 'active'].includes(promoteStatus) ? parseInt(targetClassId, 10) : null,
        academic_year_id: ['promoted', 'repeated', 'active'].includes(promoteStatus) ? parseInt(targetYearId, 10) : null
      };

      const res = await api.post('/students/promote', payload);
      if (res.data.success) {
        setIsPromoteModalOpen(false);
        setSelectedPupilIds([]);
        setLoading(true);
        fetchPupilsAndYears();
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to complete promotion transition.');
    }
  };

  if (loading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-xs font-semibold text-slate-500 text-helper">Loading pupils roster...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Header and Back navigation */}
      <div>
        <Link to="/classes" className="text-xs font-semibold text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2">
          &larr; Back to My Classes
        </Link>
        <h1 className="text-page-header flex items-center gap-2">🏫 {className} Dashboard</h1>
        <p className="text-helper mt-0.5">
          Roster total: <strong className="text-slate-800">{pupils.length} pupils</strong> enrolled in the active term.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-helper text-center">
          ⚠️ {error}
        </div>
      )}

      {/* Control panel boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Continuous Assessment Card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between space-y-4">
          <div className="space-y-1">
            <span className="text-2xl">📝</span>
            <h2 className="text-section-header">Continuous Assessment (CA)</h2>
            <p className="text-helper">
              Select parameters to launch the spreadsheet score-entry sheet.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="space-y-1">
              <label className="text-form-label uppercase tracking-wider block">Academic Year</label>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-table-body outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {academicYears.map(y => (
                  <option key={y.id} value={String(y.id)}>{y.year_name}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-1">
              <label className="text-form-label uppercase tracking-wider block">Period</label>
              <select 
                value={selectedPeriod} 
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-table-body outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="1_1">Term 1 Mid-Term</option>
                <option value="1_2">End of Term 1</option>
                <option value="2_1">Term 2 Mid-Term</option>
                <option value="2_2">End of Term 2</option>
                <option value="3_1">Term 3 Mid-Term</option>
                <option value="3_2">End of Term 3</option>
              </select>
            </div>
          </div>

          <button 
            onClick={handleLaunchGrading} 
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-xl transition-all shadow-sm text-nav mt-4"
          >
            Launch Auto-Saving Score Grid
          </button>
        </div>

        {/* Daily Attendance Card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col justify-between space-y-4">
          <div className="space-y-1">
            <span className="text-2xl">📅</span>
            <h2 className="text-section-header">Daily Attendance Roster</h2>
            <p className="text-helper">
              Select target school day to mark present or absent student grids.
            </p>
          </div>

          <div className="space-y-1 pt-2">
            <label className="text-form-label uppercase tracking-wider block">Target Date</label>
            <input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)} 
              max={new Date().toISOString().split('T')[0]}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-table-body outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          <button 
            onClick={handleLaunchAttendance} 
            className="w-full bg-white hover:bg-slate-50 border border-indigo-600 text-indigo-600 font-semibold py-2.5 rounded-xl transition-all shadow-sm text-nav mt-4"
          >
            Open Daily Attendance Grid
          </button>
        </div>
      </div>

      {/* Class pupil demographic list */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between sm:items-center gap-3">
          <h2 className="text-section-header">Enrolled Pupil Roster</h2>
          {hasPermission('manage_students') && (
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setIsRegisterModalOpen(true)} 
                className="px-3 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-colors shadow-sm"
              >
                + Register Pupil
              </button>
              {selectedPupilIds.length > 0 && (
                <button 
                  onClick={() => {
                    setTargetClassId(classesList.length > 0 ? String(classesList[0].id) : '');
                    setIsPromoteModalOpen(true);
                  }} 
                  className="px-3 py-2 text-nav font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-colors shadow-sm"
                >
                  Bulk Roll/Promote ({selectedPupilIds.length})
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* A. Responsive Card view for Mobile viewports */}
        <div className="block md:hidden p-4 space-y-3">
          {pupils.length === 0 ? (
            <p className="text-center text-helper italic text-slate-400 py-6">No enrolled pupils in this class.</p>
          ) : (
            pupils.map((pupil) => (
              <div 
                key={pupil.id} 
                className={`p-4 rounded-xl border transition-all flex items-start justify-between gap-3 ${
                  selectedPupilIds.includes(pupil.id) ? 'bg-indigo-50/50 border-indigo-300' : 'bg-white border-slate-100'
                }`}
              >
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-card-title text-indigo-900">{pupil.first_name} {pupil.last_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                      pupil.sex === 'M' ? 'bg-indigo-50 text-indigo-700' : 'bg-pink-50 text-pink-700'
                    }`}>
                      {pupil.sex}
                    </span>
                  </div>
                  <div className="text-[10px] space-y-0.5 text-slate-500">
                    <p>Adm No: <strong className="text-slate-700">{pupil.admission_number}</strong></p>
                    <p>Guardian: <strong className="text-slate-700">{pupil.parent_guardian_name || 'N/A'}</strong></p>
                    <p>Nationality: <strong className="text-slate-700">{pupil.nationality}</strong></p>
                  </div>
                </div>
                {hasPermission('manage_students') && (
                  <input 
                    type="checkbox" 
                    checked={selectedPupilIds.includes(pupil.id)} 
                    onChange={() => handleSelectPupil(pupil.id)}
                    className="h-4.5 w-4.5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 transition-all cursor-pointer mt-0.5"
                  />
                )}
              </div>
            ))
          )}
        </div>

        {/* B. Wide Table view for Desktop/Tablet viewports */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {hasPermission('manage_students') && (
                  <th className="p-3 text-center w-12">
                    <input 
                      type="checkbox" 
                      checked={selectedPupilIds.length === pupils.length && pupils.length > 0}
                      onChange={handleSelectAll} 
                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                )}
                <th className="p-3 text-left text-card-title text-slate-400 uppercase tracking-wider">Admission Number</th>
                <th className="p-3 text-left text-card-title text-slate-400 uppercase tracking-wider">First Name</th>
                <th className="p-3 text-left text-card-title text-slate-400 uppercase tracking-wider">Last Name</th>
                <th className="p-3 text-center text-card-title text-slate-400 uppercase tracking-wider">Sex</th>
                <th className="p-3 text-left text-card-title text-slate-400 uppercase tracking-wider">Guardian</th>
                <th className="p-3 text-left text-card-title text-slate-400 uppercase tracking-wider">Nationality</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pupils.length === 0 ? (
                <tr>
                  <td colSpan={hasPermission('manage_students') ? 7 : 6} className="p-6 text-center text-helper italic text-slate-400">
                    No enrolled pupils in this class.
                  </td>
                </tr>
              ) : (
                pupils.map((pupil) => (
                  <tr key={pupil.id} className="hover:bg-slate-50/60 transition-colors">
                    {hasPermission('manage_students') && (
                      <td className="p-3 text-center">
                        <input 
                          type="checkbox" 
                          checked={selectedPupilIds.includes(pupil.id)} 
                          onChange={() => handleSelectPupil(pupil.id)}
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                    )}
                    <td className="p-3 text-table-body font-bold text-indigo-950">{pupil.admission_number}</td>
                    <td className="p-3 text-table-body font-medium">{pupil.first_name}</td>
                    <td className="p-3 text-table-body font-medium">{pupil.last_name}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        pupil.sex === 'M' ? 'bg-indigo-50 text-indigo-700' : 'bg-pink-50 text-pink-700'
                      }`}>
                        {pupil.sex}
                      </span>
                    </td>
                    <td className="p-3 text-table-body text-slate-500">{pupil.parent_guardian_name || 'N/A'}</td>
                    <td className="p-3 text-table-body text-slate-500">{pupil.nationality}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Register Pupil Modal */}
      {isRegisterModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-4">Register New Student</h2>
            <form onSubmit={handleRegisterStudent} className="space-y-3.5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">First Name</label>
                  <input type="text" value={regFirstName} onChange={e => setRegFirstName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
                </div>
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">Last Name</label>
                  <input type="text" value={regLastName} onChange={e => setRegLastName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Admission Number</label>
                <input type="text" value={regAdmissionNumber} onChange={e => setRegAdmissionNumber(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">Sex</label>
                  <select value={regSex} onChange={e => setRegSex(e.target.value as 'M' | 'F')} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all">
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-form-label uppercase tracking-wider block">Date of Birth</label>
                  <input type="date" value={regDOB} onChange={e => setRegDOB(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Parent/Guardian Name</label>
                <input type="text" value={regParent} onChange={e => setRegParent(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" />
              </div>
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Nationality</label>
                <input type="text" value={regNationality} onChange={e => setRegNationality(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required />
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsRegisterModalOpen(false)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors">Register</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Promotion / Rollover Modal */}
      {isPromoteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 border border-slate-100">
            <h2 className="text-section-header mb-2">Bulk Pupil Rollover / Promotion</h2>
            <p className="text-helper text-slate-500 mb-4">
              Applying lifecycle state changes to <strong className="text-slate-800">{selectedPupilIds.length}</strong> selected student(s).
            </p>
            <form onSubmit={handleBulkPromote} className="space-y-4">
              <div className="space-y-1">
                <label className="text-form-label uppercase tracking-wider block">Transition Status</label>
                <select value={promoteStatus} onChange={e => setPromoteStatus(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all">
                  <option value="promoted">Promote (Move to Next Class/Year)</option>
                  <option value="repeated">Repeat (Re-enroll in Grade/Class)</option>
                  <option value="graduated">Graduate (Exits Institution)</option>
                  <option value="transferred">Transfer Out (Leave to another school)</option>
                  <option value="withdrawn">Withdraw (Exits School System)</option>
                </select>
              </div>

              {['promoted', 'repeated', 'active'].includes(promoteStatus) && (
                <>
                  <div className="space-y-1">
                    <label className="text-form-label uppercase tracking-wider block">Target Class</label>
                    <select value={targetClassId} onChange={e => setTargetClassId(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required>
                      <option value="">Select Target Class</option>
                      {classesList.map(c => (
                        <option key={c.id} value={c.id}>{c.class_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-form-label uppercase tracking-wider block">Target Academic Year</label>
                    <select value={targetYearId} onChange={e => setTargetYearId(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-table-body focus:border-indigo-500 focus:bg-white outline-none transition-all" required>
                      {academicYears.map(y => (
                        <option key={y.id} value={y.id}>{y.year_name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button type="button" onClick={() => setIsPromoteModalOpen(false)} className="px-4 py-2 text-nav font-bold text-slate-500 hover:text-slate-700 transition-colors">Cancel</button>
                <button type="submit" className="px-5 py-2 text-nav font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-sm transition-colors">Apply Transition</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClassDetails;
